/**
 * Reusable pieces of the "SCM credential + webhook" UX. Lifted out of
 * CreateApplicationDialog so the second wizard that needs the same
 * inputs (CreateComponentDialog's repo step) can reuse them rather than
 * duplicate ~150 lines of password input + collapsible PAT docs +
 * register-webhook checkbox + paste-into-upstream fallback panel.
 *
 * Provider-neutral: every visible piece accepts a `scmKind` prop and
 * dispatches to the right per-provider labels / links / docs via the
 * `PROVIDER_COPY` map at the bottom of this file. Default is `'github'`
 * so existing call sites keep their current behaviour when they don't
 * pass the prop.
 *
 * Nothing here knows about a specific Component or Application — the
 * helpers take small `{ ... }` arg objects and the components take
 * value/setValue props so the host dialog owns state and lifecycle.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { api, type Secret, type WebhookSetupResult } from '@/api/client.js';

/**
 * Which provider's docs / labels to render. Mirrors the SDK's `ScmKind`
 * union — duplicated as a local literal type so this file doesn't have
 * to depend on the SDK package directly (the web bundle imports the
 * SDK transitively but keeping the leaf import-light is cheap).
 */
export type ScmProviderKind = 'github' | 'gitlab' | 'bitbucket';

/* ---------------------------------------------------------------------------
 * Manual-fallback shape — what the server returns when it tried to
 * register the webhook upstream and GitHub said no. Persisted secret is
 * in here so the operator can paste it into GitHub by hand.
 * ------------------------------------------------------------------------- */

export interface ManualFallback {
  reason: string;
  deliveryUrl: string;
  secret: string;
  events: Array<'push' | 'release' | 'pull_request'>;
}

/* ---------------------------------------------------------------------------
 * PAT input + collapsible "how to create one" docs
 * ------------------------------------------------------------------------- */

interface PatFieldProps {
  value: string;
  onChange: (v: string) => void;
  /** Provider whose docs / placeholder / label to render. Defaults to 'github'. */
  scmKind?: ScmProviderKind;
  /** Label shown above the input. Defaults to provider-specific text ("GitHub Personal Access Token" etc.). */
  label?: string;
  /** Extra helper text rendered above the docs toggle. Defaults to a provider-generic blurb. */
  helperText?: string;
  /**
   * When set, the field renders a "Use existing / Paste new" radio
   * toggle above the input. Picking an existing secret hides the
   * paste input + docs and just selects the existing one. The
   * parent uses `existingPat.selectedId` to decide whether to skip
   * `createSecretFromPat` at submit time and use the picked
   * secret's id directly as `credentialsRef`.
   *
   * Optional: callers that don't need re-use (the empty-state
   * Components-card hint, etc.) just omit this prop and the field
   * keeps its single "paste a token" behaviour.
   */
  existingPat?: {
    /** Org to list secrets from. */
    orgId: string;
    /** Currently-picked existing secret id, or null when in paste mode. */
    selectedId: string | null;
    /** Called when the operator picks a different existing secret (or `null` to switch back to paste mode). */
    onSelect: (id: string | null) => void;
  };
}

/**
 * Password input for an SCM PAT with a "How do I create one?" toggle
 * that expands a per-provider guide.
 *
 * When `existingPat` is set, also offers a "Use an existing PAT"
 * picker — populated from the org's secrets filtered to ones tagged
 * as a PAT for this provider in `metadata.kind`. The picker only
 * appears when at least one matching secret exists; otherwise the
 * field renders as if `existingPat` weren't passed (so the operator
 * isn't shown an empty dropdown).
 *
 * Defaults render GitHub copy so call sites that haven't been
 * updated to pass `scmKind` keep working unchanged.
 */
export function PatField({
  value,
  onChange,
  scmKind = 'github',
  label,
  helperText = 'Used for private-repo clones and registering the webhook below. Stored encrypted via SecretsService — never shown again after this step.',
  existingPat,
}: PatFieldProps): JSX.Element {
  const [showHelp, setShowHelp] = useState(false);
  const copy = PROVIDER_COPY[scmKind];
  const resolvedLabel = label ?? `${copy.name} Personal Access Token`;

  // Query existing PAT secrets for this org + provider. Filtered
  // client-side on `metadata.kind === '${scmKind}-pat'` — the same
  // tag `createSecretFromPat` writes, so any PAT the wizard has
  // ever stored shows up here. Disabled when the caller didn't
  // pass `existingPat` (no re-use UX needed on that callsite).
  const existingPatQuery = useQuery({
    queryKey: ['org-secrets', existingPat?.orgId, scmKind],
    queryFn: async () => api.secrets.listForOrg(existingPat!.orgId),
    enabled: !!existingPat,
    staleTime: 30_000,
  });

  const matchingSecrets: Secret[] =
    existingPatQuery.data?.filter((s) => s.metadata?.['kind'] === `${scmKind}-pat`) ?? [];

  // Only show the picker when there are secrets to pick from.
  // Showing an empty <select> with just "Paste new" would be more
  // confusing than just rendering the paste-only flow.
  const showPicker = Boolean(existingPat) && matchingSecrets.length > 0;
  const mode: 'paste' | 'existing' =
    showPicker && existingPat?.selectedId != null ? 'existing' : 'paste';

  return (
    <div className="space-y-1.5">
      <Label htmlFor="pat">{resolvedLabel}</Label>

      {showPicker && (
        <ExistingPatPicker
          providerName={copy.name}
          secrets={matchingSecrets}
          selectedId={existingPat!.selectedId}
          onSelectExisting={(id) => {
            existingPat!.onSelect(id);
            // Clear the paste field when switching to an existing
            // secret — the value would otherwise be dead state.
            if (id) onChange('');
          }}
          onSwitchToPaste={() => existingPat!.onSelect(null)}
        />
      )}

      {mode === 'paste' && (
        <>
          <Input
            id="pat"
            type="password"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={copy.tokenPlaceholder}
            autoComplete="off"
          />
          {/* `min-w-0` on the <p> is load-bearing: without it, flex
              items default to `min-width: auto` (= the content's natural
              width), which for this long helper text exceeds the dialog
              width and busts the modal sideways — the Hide button ends up
              rendered past the right edge. `min-w-0` lets the paragraph
              shrink and wrap naturally. */}
          <div className="flex items-center justify-between gap-2">
            <p className="min-w-0 text-xs text-muted-foreground">{helperText}</p>
            <button
              type="button"
              onClick={() => setShowHelp((v) => !v)}
              className="shrink-0 text-xs text-primary hover:underline"
            >
              {showHelp ? 'Hide' : 'How do I create one?'}
            </button>
          </div>
          {showHelp && <PatHelpPanel scmKind={scmKind} />}
        </>
      )}
    </div>
  );
}

/**
 * Dropdown of existing PAT secrets + a "Paste a new one instead"
 * affordance. Rendered inside `PatField` when the caller wired
 * `existingPat` and the org has at least one matching secret.
 *
 * The select is keyed by secret id (Secret rows are immutable
 * id-wise; rename doesn't change id) so a rename in another tab
 * doesn't disturb the picked value. Last-used timestamp is shown
 * in muted text after the name so an operator with several PATs
 * can pick the one they're actively using.
 */
function ExistingPatPicker({
  providerName,
  secrets,
  selectedId,
  onSelectExisting,
  onSwitchToPaste,
}: {
  providerName: string;
  secrets: Secret[];
  selectedId: string | null;
  onSelectExisting: (id: string) => void;
  onSwitchToPaste: () => void;
}): JSX.Element {
  // Sort: most-recently-used first, then alphabetical by name.
  // `lastUsedAt` is null for never-used secrets — those sink to
  // the bottom so the operator's active PAT bubbles to the top.
  const sorted = [...secrets].sort((a, b) => {
    const aTs = a.lastUsedAt ? Date.parse(a.lastUsedAt) : 0;
    const bTs = b.lastUsedAt ? Date.parse(b.lastUsedAt) : 0;
    if (aTs !== bTs) return bTs - aTs;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="space-y-1.5 rounded-md border border-border bg-muted/40 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="existing-pat" className="text-xs font-medium">
          Use an existing {providerName} PAT
        </Label>
        {selectedId != null && (
          <button
            type="button"
            onClick={onSwitchToPaste}
            className="shrink-0 text-xs text-primary hover:underline"
          >
            Or paste a new one
          </button>
        )}
      </div>
      <select
        id="existing-pat"
        value={selectedId ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '__paste__') {
            onSwitchToPaste();
          } else if (v) {
            onSelectExisting(v);
          }
        }}
        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="">— Pick a saved PAT —</option>
        {sorted.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
            {s.lastUsedAt
              ? ` · last used ${new Date(s.lastUsedAt).toLocaleDateString()}`
              : ' · never used'}
          </option>
        ))}
        <option value="__paste__">Paste a new PAT instead…</option>
      </select>
    </div>
  );
}

interface PatHelpPanelProps {
  /** Provider whose docs to render. Defaults to 'github'. */
  scmKind?: ScmProviderKind;
}

/**
 * The expanding "create a PAT" guide. Dispatches to per-provider
 * copy via `PROVIDER_COPY`. Extracted as its own component so a host
 * that wants to show it without the input (e.g. an empty state on
 * the Components card) can render it directly.
 */
export function PatHelpPanel({ scmKind = 'github' }: PatHelpPanelProps = {}): JSX.Element {
  const copy = PROVIDER_COPY[scmKind];
  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
      <p className="font-medium text-foreground">
        Create a {copy.name} PAT in 60 seconds:
      </p>
      <ol className="ml-4 list-decimal space-y-1">
        <li>
          Open{' '}
          <a
            href={copy.tokenCreateUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
          >
            {copy.tokenCreateDisplay}
          </a>{' '}
          → <strong>{copy.generateButtonLabel}</strong>
          {copy.tokenStyleHint ? <> ({copy.tokenStyleHint})</> : null}.
        </li>
        {copy.repoAccessStep ? (
          <li>
            <strong>{copy.repoAccessStep.label}:</strong> {copy.repoAccessStep.body}
          </li>
        ) : null}
        <li>
          <strong>{copy.scopesStep.label}:</strong>
          <ul className="ml-4 list-disc">
            {copy.scopesStep.scopes.map((s) => (
              <li key={s.name}>
                <code className="rounded bg-background px-1">{s.name}</code>
                {s.note ? ` — ${s.note}` : null}
              </li>
            ))}
          </ul>
        </li>
        <li>
          Click <strong>{copy.generateButtonLabel}</strong>, copy it, paste it above.
        </li>
      </ol>
      {copy.fallbackNote ? (
        <p className="text-[11px]">{copy.fallbackNote}</p>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * "Register webhook on GitHub now" checkbox
 * ------------------------------------------------------------------------- */

interface RegisterWebhookCheckboxProps {
  checked: boolean;
  onChange: (b: boolean) => void;
  /** When falsy, the checkbox is disabled with a "need a PAT" hint. */
  hasPat: boolean;
  /** Provider whose name to use in the label. Defaults to 'github'. */
  scmKind?: ScmProviderKind;
}

export function RegisterWebhookCheckbox({
  checked,
  onChange,
  hasPat,
  scmKind = 'github',
}: RegisterWebhookCheckboxProps): JSX.Element {
  const copy = PROVIDER_COPY[scmKind];
  return (
    <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={!hasPat}
        className="mt-0.5"
      />
      <span className="flex-1">
        <span className="font-medium">Register webhook on {copy.name} now</span>
        <span className="ml-2 text-xs text-muted-foreground">
          Pushes to the tracked branch trigger discovery + notify app admins.
        </span>
        {!hasPat && (
          <span className="mt-0.5 block text-[11px] text-muted-foreground">
            Needs a PAT with webhook write permission. Add one above to enable.
          </span>
        )}
      </span>
    </label>
  );
}

/* ---------------------------------------------------------------------------
 * Manual-paste fallback panel + copyable field helper
 * ------------------------------------------------------------------------- */

interface ManualWebhookFallbackPanelProps {
  fallback: ManualFallback;
  onDone: () => void;
  /** Provider whose UI step names + button labels to use. Defaults to 'github'. */
  scmKind?: ScmProviderKind;
}

/**
 * Rendered when the server tried to register the webhook upstream and
 * the provider said no (auth, missing scope, rate limit…). The
 * Component + signing secret are persisted server-side already — the
 * operator just needs to paste the webhook into the upstream by hand.
 * We show the URL, the secret (once — never queryable again), the
 * event list, and a Done button to acknowledge + continue. Per-
 * provider button labels come from `PROVIDER_COPY.manualWebhook`.
 */
export function ManualWebhookFallbackPanel({
  fallback,
  onDone,
  scmKind = 'github',
}: ManualWebhookFallbackPanelProps): JSX.Element {
  const copy = PROVIDER_COPY[scmKind];
  const m = copy.manualWebhook;
  return (
    <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-3 text-sm">
      <div className="font-medium">
        Webhook setup didn't complete on {copy.name}'s side.
      </div>
      <div className="text-xs text-muted-foreground">{fallback.reason}</div>
      <div className="space-y-1.5 pt-1">
        <p className="text-xs font-medium text-foreground">
          Paste this into {copy.name} yourself — discovery + notifications start working the
          moment the first push arrives:
        </p>
        <ol className="ml-4 list-decimal space-y-1 text-xs">
          <li>
            Open <strong>{m.openSettingsPath}</strong> on your repo.
          </li>
          <li>
            <strong>{m.payloadUrlLabel}:</strong>
            <CopyableField value={fallback.deliveryUrl} />
          </li>
          {m.contentTypeNote ? (
            <li>
              <strong>Content type:</strong>{' '}
              <code className="rounded bg-background px-1">{m.contentTypeNote}</code>
            </li>
          ) : null}
          <li>
            <strong>{m.secretLabel}:</strong>
            <CopyableField value={fallback.secret} mono />
            <span className="block text-[11px] text-muted-foreground">
              Already saved on this Component — shown here once so you can paste it. We can't show
              it again after you close this dialog.
            </span>
          </li>
          <li>
            <strong>{m.eventsLabel}:</strong> {m.eventsInstruction}{' '}
            {fallback.events.map((e, i) => (
              <span key={e}>
                {i > 0 ? ', ' : ''}
                <code className="rounded bg-background px-1">{e}</code>
              </span>
            ))}
            .
          </li>
          <li>
            Click <strong>{m.confirmButton}</strong>.
          </li>
        </ol>
      </div>
      <div className="pt-1">
        <Button type="button" size="sm" onClick={onDone}>
          Done — continue
        </Button>
      </div>
    </div>
  );
}

/**
 * Tiny inline "click to copy" pill. No tooltip lib needed — we just
 * swap the label briefly via a setTimeout.
 */
export function CopyableField({ value, mono }: { value: string; mono?: boolean }): JSX.Element {
  const [copied, setCopied] = useState(false);
  function copy() {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <button
      type="button"
      onClick={copy}
      className={`mt-0.5 flex w-full items-center justify-between gap-2 rounded border border-border bg-background px-2 py-1 text-left text-xs hover:bg-accent/40 ${
        mono ? 'font-mono' : ''
      }`}
    >
      <span className="truncate">{value}</span>
      <span className="shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground">
        {copied ? 'copied' : 'copy'}
      </span>
    </button>
  );
}

/* ---------------------------------------------------------------------------
 * Submission helpers
 * ------------------------------------------------------------------------- */

/**
 * Create a Secret on the org from a pasted PAT. Returns the new
 * secret's id (caller assigns it as `credentialsRef`). Pass a stable
 * `name` so a repeat invocation doesn't pile up identical-looking
 * secrets — caller decides what's appropriate (e.g. `<app-slug>-github-pat`).
 *
 * Returns undefined when `pat` is empty / whitespace-only.
 */
export async function createSecretFromPat(input: {
  orgId: string;
  name: string;
  pat: string;
  /** Which provider this PAT is for. Drives the metadata `kind` field so
   *  audit + reuse hints stay accurate when DeployIt grows non-GitHub
   *  providers. Defaults to `'github'`. */
  scmKind?: ScmProviderKind;
  /** Optional applicationId to scope the secret to this app. */
  applicationId?: string;
  /** Optional componentId to scope the secret to this component. */
  componentId?: string;
  /** Free-form metadata persisted alongside the secret (audit / debugging). */
  metadata?: Record<string, unknown>;
}): Promise<string | undefined> {
  const trimmed = input.pat.trim();
  if (!trimmed) return undefined;
  const kind = input.scmKind ?? 'github';
  const secret = await api.secrets.create(input.orgId, {
    mode: 'paste',
    name: input.name,
    plaintext: trimmed,
    metadata: { source: 'wizard', kind: `${kind}-pat`, ...input.metadata },
    ...(input.applicationId ? { applicationId: input.applicationId } : {}),
    ...(input.componentId ? { componentId: input.componentId } : {}),
  });
  return secret.id;
}

/**
 * Try to register a webhook on a Component. Returns a discriminated
 * outcome the host dialog uses to render a toast / set fallback state
 * / navigate.
 *
 * Why this lives here: the SAME three-branch outcome (configured /
 * manual-fallback / unexpected error) drives both the new-app wizard
 * and the add-component dialog. Keeping the dispatch in one place
 * means there's exactly one source of truth for "what does the
 * operator see when the webhook can't be registered".
 */
export type AttemptWebhookOutcome =
  | { kind: 'configured'; rotated: boolean }
  | { kind: 'manual-fallback'; fallback: ManualFallback }
  | { kind: 'unexpected-error'; message: string };

export async function attemptWebhookSetup(componentId: string): Promise<AttemptWebhookOutcome> {
  try {
    const result: WebhookSetupResult = await api.components.setupWebhook(componentId);
    if (result.status === 'configured') {
      return { kind: 'configured', rotated: result.rotated };
    }
    return {
      kind: 'manual-fallback',
      fallback: {
        reason: result.reason,
        deliveryUrl: result.deliveryUrl,
        secret: result.secret,
        events: result.events,
      },
    };
  } catch (err) {
    return {
      kind: 'unexpected-error',
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

/* ---------------------------------------------------------------------------
 * Per-provider copy
 *
 * The single source of truth for every provider-specific string in this
 * file. Adding a new provider implementation: add an entry here, pass
 * the new `ScmProviderKind` literal through, and every visible piece
 * picks up the right labels / links. Keep entries plain strings (no
 * JSX) so the renderers stay simple and copy stays easy to grep.
 * ------------------------------------------------------------------------- */

interface ProviderCopy {
  /** Display name — used in labels, panel headings, button text. */
  name: string;
  /** Placeholder text for the PAT input — provider-typical token prefix. */
  tokenPlaceholder: string;
  /** URL the "How do I create one?" link opens. */
  tokenCreateUrl: string;
  /** What to show as the link's visible text. */
  tokenCreateDisplay: string;
  /** Wording on the upstream's create-token button (varies). */
  generateButtonLabel: string;
  /** Optional parenthetical about token style (fine-grained vs classic etc.). */
  tokenStyleHint?: string;
  /**
   * Optional "Repository access" step — GitHub fine-grained tokens have one,
   * GitLab / Bitbucket don't (scope is global to the user / project).
   */
  repoAccessStep?: { label: string; body: string };
  /** Required-scopes step. Every provider needs one of these. */
  scopesStep: {
    label: string;
    scopes: Array<{ name: string; note?: string }>;
  };
  /** Optional footer note (e.g. classic-token fallback for GitHub). */
  fallbackNote?: string;
  /** Strings used by the manual-paste fallback panel. */
  manualWebhook: {
    /** UI path the operator follows in the upstream to add a webhook. */
    openSettingsPath: string;
    /** Label the upstream uses for the payload URL field. */
    payloadUrlLabel: string;
    /** Optional content-type guidance — GitHub has a content-type picker, others assume JSON. */
    contentTypeNote?: string;
    /** Label the upstream uses for the secret field. */
    secretLabel: string;
    /** Label for the events selector. */
    eventsLabel: string;
    /** Wording right before the event list. */
    eventsInstruction: string;
    /** Button text on the upstream's confirm CTA. */
    confirmButton: string;
  };
}

const PROVIDER_COPY: Record<ScmProviderKind, ProviderCopy> = {
  github: {
    name: 'GitHub',
    tokenPlaceholder: 'ghp_…',
    tokenCreateUrl: 'https://github.com/settings/tokens?type=beta',
    tokenCreateDisplay: 'github.com/settings/tokens',
    generateButtonLabel: 'Generate new token',
    tokenStyleHint: 'fine-grained tokens are best',
    repoAccessStep: {
      label: 'Repository access',
      body:
        'pick the repo this app lives in (or "All repositories" for an integrator-wide token).',
    },
    scopesStep: {
      label: 'Permissions (Repository)',
      scopes: [
        { name: 'Contents', note: 'Read-only (for shallow clones during discovery)' },
        { name: 'Webhooks', note: 'Read & write (to register the webhook below)' },
        { name: 'Metadata', note: 'Read-only (automatic; GitHub adds it)' },
      ],
    },
    fallbackNote:
      'Classic tokens work too — they need `repo` + `admin:repo_hook`. Skip the PAT for a public repo (discovery still works; webhook setup will be unavailable).',
    manualWebhook: {
      openSettingsPath: 'Settings → Webhooks → Add webhook',
      payloadUrlLabel: 'Payload URL',
      contentTypeNote: 'application/json',
      secretLabel: 'Secret',
      eventsLabel: 'Which events',
      eventsInstruction: '"Let me select individual events" → tick',
      confirmButton: 'Add webhook',
    },
  },
  gitlab: {
    name: 'GitLab',
    tokenPlaceholder: 'glpat-…',
    tokenCreateUrl: 'https://gitlab.com/-/user_settings/personal_access_tokens',
    tokenCreateDisplay: 'gitlab.com/-/user_settings/personal_access_tokens',
    generateButtonLabel: 'Add new token',
    scopesStep: {
      label: 'Scopes',
      scopes: [
        { name: 'read_repository', note: 'shallow clones during discovery' },
        { name: 'api', note: 'register the webhook below' },
      ],
    },
    manualWebhook: {
      openSettingsPath: 'Settings → Webhooks → Add new webhook',
      payloadUrlLabel: 'URL',
      secretLabel: 'Secret token',
      eventsLabel: 'Trigger',
      eventsInstruction: 'tick',
      confirmButton: 'Add webhook',
    },
  },
  bitbucket: {
    name: 'Bitbucket',
    tokenPlaceholder: 'ATBB…',
    tokenCreateUrl: 'https://bitbucket.org/account/settings/app-passwords/',
    tokenCreateDisplay: 'bitbucket.org/account/settings/app-passwords',
    generateButtonLabel: 'Create app password',
    scopesStep: {
      label: 'Permissions',
      scopes: [
        { name: 'Repositories: Read', note: 'shallow clones during discovery' },
        { name: 'Webhooks: Read and write', note: 'register the webhook below' },
      ],
    },
    manualWebhook: {
      openSettingsPath: 'Repository settings → Webhooks → Add webhook',
      payloadUrlLabel: 'URL',
      secretLabel: 'Secret',
      eventsLabel: 'Triggers',
      eventsInstruction: 'tick',
      confirmButton: 'Save',
    },
  },
};
